/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { EmployeeModel } from '../../../models/employee';
import { RoleAndPermissionModel } from '../../../models/roleAndPermission';
import { RootState } from '../../../store';
import { AdminEnumType } from '../../../enums/adminEnum';
import { DEFAULT_ROWS_PER_PAGE } from '../../../config-global';
import {
  EMPLOYEE_DELETE,
  EMPLOYEE_FIND_MANY,
  FIND_ROLE_AND_PERMISSION,
} from '../../../lib/endpoints/employee';
import {
  OFFICER_PROFILE_DELETE,
  OFFICER_PROFILE_FIND_MANY,
} from '../../../lib/endpoints/officerProfile';
import axios from '../../../lib/axios';
import { OfficerProfileModel } from '../../../models/officeProfile';
import { EMPLOYEE_CHANGE_ADMIN_PASSWORD } from '../../../lib/endpoints/auth';
import { MERCHANT_STORE_FIND_MANY } from '../../../lib/endpoints/merchant';
import { MerChantModel } from '../../../models/merChantModel';

export const useAdminAccountController = () => {
  // ------------ State & Root State --------------//
  const [data, setData] = useState<EmployeeModel[]>([]);
  const [roleData, setRoleData] = useState<RoleAndPermissionModel[]>([]);
  const [merchantStoreData, setMerchantStoreData] = useState<MerChantModel[]>([]);
  const [selectedMainId, setSelectedMainId] = useState<string>(null!);
  const [selectedProfileId, setSelectedProfileId] = useState<string>(null!);
  const [selectedAdminData, setSelectedAdminData] = useState<any>(null!);
  const [searchValue, setSearchValue] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [openMenu, setOpenMenu] = useState(null);
  const [openForm, setOpenForm] = useState<boolean>(false);
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const [loadingDeleteButton, setLoadingDeleteButton] = useState<boolean>(false);
  const [openDeleteForm, setOpenDeleteForm] = useState<boolean>(false);
  const [openChangePassword, setOpenChangePassword] = useState<boolean>(false);
  const [loadingConfirmButton, setLoadingConfirmButton] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>(null!);

  const ownerPassword = useRef<HTMLInputElement>(null!);
  const newPassword = useRef<HTMLInputElement>(null!);
  const confirmNewPassword = useRef<HTMLInputElement>(null!);

  const auth = useSelector((state: RootState) => state.auth);
  const adminId = auth?.data?.id;
  const roleAdmin = auth?.data?.role?.role_type;

  const isSuperAdmin = roleAdmin === 'SUPER_ADMIN';
  const initialTabValue: AdminEnumType = isSuperAdmin
    ? AdminEnumType.SUPER_ADMIN
    : AdminEnumType.ADMIN;
  const [tabAdminPanel, setTabAdminPanel] = useState<AdminEnumType>(initialTabValue);

  const { t } = useTranslation('global');

  // ------------ State & variable End--------------//

  // -------------- Call Api ---------------------//

  const handleFetchData = async () => {
    try {
      setLoadingData(true);
      const employeeResponse = await axios.post(`${EMPLOYEE_FIND_MANY}?limit=${100000}`);
      const officerResponse = await axios.get(`${OFFICER_PROFILE_FIND_MANY}?take=${100000}`);
      const roleResponse = await axios.post(FIND_ROLE_AND_PERMISSION);
      const resMerchant = await axios.get(MERCHANT_STORE_FIND_MANY);

      const roleData = roleResponse.data;
      const officerProfileData = officerResponse.data;

      const roleType = roleData.reduce((obj: any, role: any) => {
        obj[role.id] = role.name;
        return obj;
      }, {});

      const updatedData = employeeResponse?.data.map((employee: any) => ({
        ...employee,
        role_type: roleType[employee.roleId],
      }));

      setData(
        updatedData?.map((data: EmployeeModel) => {
          const populateOfficer: EmployeeModel[] = officerProfileData.filter(
            (subData: OfficerProfileModel) => subData?.self_id === data?.id
          );
          return {
            ...data,
            profile: populateOfficer?.length > 0 ? populateOfficer[0] : null!,
          };
        })
      );
      setRoleData(
        roleData.filter((item: RoleAndPermissionModel) => {
          if (roleAdmin === 'SUPER_ADMIN') {
            return item?.role_type === 'SUPER_ADMIN' || item?.role_type === 'ADMIN';
          } else if (roleAdmin === 'ADMIN') {
            return item?.role_type === 'ADMIN';
          }
          return false;
        })
      );
      if (resMerchant?.data?.length > 0) {
        setMerchantStoreData(resMerchant?.data);
      }
    } catch (err: any) {
      try {
        window.alert(err.response.data.message);
      } catch (err2) {
        window.alert('Server error please try again');
      }
    } finally {
      setLoadingData(false);
    }
  };

  const handleDeleteAdminData = async () => {
    try {
      setOpenMenu(null);
      setLoadingDeleteButton(true);
      await Promise.all([
        axios.delete(EMPLOYEE_DELETE + selectedMainId),
        axios.delete(OFFICER_PROFILE_DELETE + selectedProfileId),
      ]);
      await handleFetchData();
      setOpenDeleteForm(false);
    } catch (err: any) {
      try {
        window.alert(err.response.data.message);
      } catch (err2) {
        window.alert('Server error please try again');
      }
    } finally {
      setLoadingDeleteButton(false);
      setOpenMenu(null);
    }
  };

  const handleChangePassword = async () => {
    try {
      setLoadingConfirmButton(true);
      await axios.post(
        `${EMPLOYEE_CHANGE_ADMIN_PASSWORD}?ownerPassword=${ownerPassword.current?.value}&otherAdminId=${selectedMainId}&NewOtherAdminPassword=${newPassword.current?.value}`
      );
      window.alert('ປ່ຽນລະຫັດຜ່ານສຳເລັດເເລ້ວ');
      setOpenChangePassword(false);
      setOpenMenu(null);
    } catch (error: any) {
      try {
        const getErrorMessage: string = error.response.data.message;
        setErrorMessage(getErrorMessage);
      } catch (err) {
        setErrorMessage(t('Server error please try again') || '');
      }
    } finally {
      setLoadingConfirmButton(false);
      setOpenMenu(null);
    }
  };

  // -------------- Call Api End ---------------------//

  // -------------- function ---------------------//

  const handleOpenMenu = (event: any, getId: string, profileId: string) => {
    setOpenMenu(event.currentTarget);
    setSelectedMainId(getId);
    setSelectedProfileId(profileId);
  };

  const handleOpenEditForm = () => {
    setOpenForm(true);
    const getData = data.filter((data) => data.id === selectedMainId)[0];
    setSelectedAdminData(getData);
  };

  const handleSubmit = () => {
    if (handleValidate()) {
      handleChangePassword();
    }
  };

  const handleValidate = () => {
    if (!ownerPassword.current?.value) {
      setErrorMessage('Please enter owner password.');
      return false;
    }
    if (!newPassword.current?.value) {
      setErrorMessage('Please enter new password.');
      return false;
    }
    if (!confirmNewPassword.current?.value) {
      setErrorMessage('Please enter confirm new password.');
      return false;
    }
    if (newPassword.current?.value !== confirmNewPassword.current?.value) {
      setErrorMessage('Password and Confirm Password do not match.');
      return false;
    }
    setErrorMessage('');
    return true;
  };

  // -------------- function End ---------------------//

  // -------------- Filter , search , pagination ---------------------//

  const filterAdminByStatus = data.filter((admin) => {
    if (tabAdminPanel === AdminEnumType.SUPER_ADMIN) {
      return admin?.role_type === 'Super_admin';
    } else if (tabAdminPanel === AdminEnumType.ADMIN) {
      return admin.role_type === 'Admin';
    }
    return false;
  });

  const filterData = filterAdminByStatus.filter(
    (emp) =>
      emp?.username?.toLowerCase()?.includes(searchValue.toLocaleLowerCase()) ||
      emp?.profile?.fullName?.toLowerCase()?.includes(searchValue.toLowerCase()) ||
      emp?.profile?.email?.toLowerCase()?.includes(searchValue.toLowerCase())
  );

  const isSearchNotFound: boolean = !filterData.length && !!searchValue;

  const rows = filterData.slice(
    (currentPage - 1) * DEFAULT_ROWS_PER_PAGE,
    currentPage * DEFAULT_ROWS_PER_PAGE
  );

  const countPagination = Math.ceil(filterData.length / DEFAULT_ROWS_PER_PAGE);

  // -------------- Filter , search , pagination End---------------------//

  // -------------- useEffect -------------- //
  useEffect(() => {
    handleFetchData();
  }, []);
  // -------------- useEffect End -------------- //

  return {
    searchValue,
    openMenu,
    handleOpenMenu,
    openForm,
    currentPage,
    rows,
    loadingData,
    roleData,
    handleOpenEditForm,
    selectedAdminData,
    handleFetchData,
    handleDeleteAdminData,
    loadingDeleteButton,
    openDeleteForm,
    setSelectedAdminData,
    countPagination,
    data,
    isSearchNotFound,
    setTabAdminPanel,
    tabAdminPanel,
    filterAdminByStatus,
    openChangePassword,
    handleChangePassword,
    handleSubmit,
    loadingConfirmButton,
    errorMessage,
    ownerPassword,
    newPassword,
    confirmNewPassword,
    selectedMainId,
    adminId,
    isSuperAdmin,
    setCurrentPage,
    merchantStoreData,
    handleCloseMenu: () => {
      setOpenMenu(null);
      setSelectedMainId(null!);
      setSelectedProfileId(null!);
    },
    handleSearch: (e: React.ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value),
    handleCloseForm: () => {
      setOpenForm(false);
      setOpenMenu(null);
    },
    handleOpenForm: () => setOpenForm(true),
    handleChangePage: (_: any, newPage: any) => setCurrentPage(newPage),
    handleCloseDeleteForm: () => setOpenDeleteForm(false),
    handleOpenDeleteForm: () => setOpenDeleteForm(true),
    handleOpenChangePasswordForm: () => setOpenChangePassword(true),
    handleCloseChangePasswordForm: () => {
      setErrorMessage('');
      setOpenChangePassword(false);
      setOpenMenu(null);
    },
  };
};
